class SiteCartDrawer extends HTMLElement {
  static FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  connectedCallback() {
    this.overlay = this.querySelector("[data-cart-overlay]");
    this.panel = this.querySelector("[data-cart-panel]");
    this.liveRegion = this.querySelector("[data-cart-live]");
    this.triggerElement = null;

    this.handleClose = this.close.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleClick = this.onClick.bind(this);
    this.handleDocClick = this.onDocClick.bind(this);
    this.handleFormSubmit = this.onFormSubmit.bind(this);

    this.overlay.addEventListener("click", this.handleClose);
    this.addEventListener("click", this.handleClick);
    document.addEventListener("click", this.handleDocClick);
    document.addEventListener("submit", this.handleFormSubmit);
  }

  disconnectedCallback() {
    this.overlay.removeEventListener("click", this.handleClose);
    this.removeEventListener("click", this.handleClick);
    document.removeEventListener("click", this.handleDocClick);
    document.removeEventListener("submit", this.handleFormSubmit);
    document.removeEventListener("keydown", this.handleKeydown);
  }

  // -- Accessibility helpers --

  announce(message) {
    if (!this.liveRegion) return;
    // Clear then set to ensure repeated announcements are spoken
    this.liveRegion.textContent = "";
    requestAnimationFrame(() => {
      this.liveRegion.textContent = message;
    });
  }

  setLoading(loading) {
    if (loading) {
      this.setAttribute("data-loading", "");
      this.panel.setAttribute("aria-busy", "true");
    } else {
      this.removeAttribute("data-loading");
      this.panel.setAttribute("aria-busy", "false");
    }
  }

  trapFocus(e) {
    const focusable = [
      ...this.panel.querySelectorAll(SiteCartDrawer.FOCUSABLE),
    ];
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // -- Open / Close --

  open() {
    this.setAttribute("data-open", "");
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", this.handleKeydown);

    // Focus the close button after the slide-in transition completes
    const onTransitionEnd = () => {
      this.panel.removeEventListener("transitionend", onTransitionEnd);
      const closeBtn = this.querySelector("[data-cart-close]");
      if (closeBtn) closeBtn.focus();
    };
    this.panel.addEventListener("transitionend", onTransitionEnd);
  }

  close() {
    this.removeAttribute("data-open");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", this.handleKeydown);

    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null;
    }
  }

  // -- Event handlers --

  onKeydown(e) {
    if (e.key === "Escape") {
      this.close();
      return;
    }
    if (e.key === "Tab") {
      this.trapFocus(e);
    }
  }

  onDocClick(e) {
    const toggle = e.target.closest("[data-cart-toggle]");
    if (!toggle) return;
    e.preventDefault();
    this.triggerElement = toggle;
    this.open();
  }

  async onFormSubmit(e) {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute("data-loading", "");
      // Toggle icon/spinner for product card quick-add buttons
      const addIcon = submitBtn.querySelector("[data-add-icon]");
      const addSpinner = submitBtn.querySelector("[data-add-spinner]");
      if (addIcon) addIcon.setAttribute("data-loading", "");
      if (addSpinner) addSpinner.setAttribute("data-loading", "");
    }

    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        body: new FormData(form),
      });

      if (!response.ok) throw new Error("Add to cart failed");

      this.setLoading(true);
      await this.refresh();
      this.setLoading(false);
      this.triggerElement = submitBtn;
      this.announce(this.dataset.msgAdded);
      this.open();
    } catch (error) {
      console.error("Cart add error:", error);
      form.submit();
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute("data-loading");
        const addIcon = submitBtn.querySelector("[data-add-icon]");
        const addSpinner = submitBtn.querySelector("[data-add-spinner]");
        if (addIcon) addIcon.removeAttribute("data-loading");
        if (addSpinner) addSpinner.removeAttribute("data-loading");
      }
    }
  }

  onClick(e) {
    if (e.target.closest("[data-cart-close]")) {
      this.close();
      return;
    }

    const minus = e.target.closest("[data-cart-quantity-minus]");
    if (minus) {
      const item = minus.closest("[data-cart-item]");
      const qty = parseInt(
        item.querySelector("[data-cart-quantity]").textContent.trim(),
      );
      const newQty = qty > 1 ? qty - 1 : 0;
      this.updateQuantity(
        minus.dataset.cartQuantityMinus,
        newQty,
        newQty === 0,
      );
      return;
    }

    const plus = e.target.closest("[data-cart-quantity-plus]");
    if (plus) {
      const item = plus.closest("[data-cart-item]");
      const qty = parseInt(
        item.querySelector("[data-cart-quantity]").textContent.trim(),
      );
      this.updateQuantity(plus.dataset.cartQuantityPlus, qty + 1);
      return;
    }

    const remove = e.target.closest("[data-cart-remove]");
    if (remove) {
      this.updateQuantity(remove.dataset.cartRemove, 0, true);
      return;
    }
  }

  // -- Cart API --

  async updateQuantity(key, quantity, isRemoval = false) {
    this.setLoading(true);

    try {
      await fetch(window.Shopify.routes.root + "cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.refresh();
      this.announce(
        isRemoval ? this.dataset.msgRemoved : this.dataset.msgUpdated,
      );
    } catch (error) {
      console.error("Cart update error:", error);
    } finally {
      this.setLoading(false);
    }
  }

  async refresh() {
    try {
      const response = await fetch(
        window.Shopify.routes.root + "?sections=cart-drawer",
      );
      const data = await response.json();
      const html = new DOMParser().parseFromString(
        data["cart-drawer"],
        "text/html",
      );
      const newContent = html.querySelector("[data-cart-inner]");
      if (newContent) {
        this.querySelector("[data-cart-inner]").innerHTML =
          newContent.innerHTML;
      }
      document.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (error) {
      console.error("Cart refresh error:", error);
    }
  }
}

if (!customElements.get("site-cart-drawer")) {
  customElements.define("site-cart-drawer", SiteCartDrawer);
}
